import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { TIER_ORDER, TIER_META } from "../../lib/tiers";
import { useTripTypes } from "../../lib/tripTypes";

const empty = { tier: "domestic", title: "", location: "", description: "", image_url: "", includes: "", nights: 5,
  category: "vacation", broker_price: "", available_from: "", available_to: "", trip_type: "weekend", country: "United States", states: "" };

export const PackageForm = ({ open, onOpenChange, initial, budgets, onSave }) => {
  const [f, setF] = useState(empty);
  const tripTypes = useTripTypes();
  useEffect(() => {
    setF(initial ? { ...empty, ...initial, includes: (initial.includes || []).join(", "), states: (initial.states || []).join(", ") } : empty);
  }, [initial, open]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const input = (k, label, props = {}) => (
    <label className="block"><span className="text-xs text-slate-400">{label}</span>
      <input data-testid={`pkg-form-${k}`} value={f[k]} onChange={set(k)} className="lux-input mt-1" {...props} /></label>
  );
  const submit = (e) => {
    e.preventDefault();
    onSave({
      tier: f.tier, title: f.title, location: f.location, description: f.description, image_url: f.image_url,
      category: f.category, nights: Number(f.nights), broker_price: Number(f.broker_price),
      available_from: f.available_from, available_to: f.available_to, trip_type: f.trip_type, country: f.country,
      states: f.country === "United States" ? String(f.states || "").split(",").map((x) => x.trim()).filter(Boolean) : [],
      includes: f.includes.split(",").map((s) => s.trim()).filter(Boolean),
    });
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="package-form" className="bg-[#13192B] border-white/10 text-slate-100 max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif-lux text-2xl">{initial ? "Edit package" : "Submit a package"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="text-xs text-slate-400">Line</span>
              <select data-testid="pkg-form-tier" value={f.tier} onChange={set("tier")} className="lux-input mt-1">
                {TIER_ORDER.map((t) => <option key={t} value={t}>{TIER_META[t].short}</option>)}
              </select></label>
            <label className="block"><span className="text-xs text-slate-400">Type</span>
              <select data-testid="pkg-form-category" value={f.category} onChange={set("category")} className="lux-input mt-1">
                <option value="vacation">Vacation</option><option value="concert">Concert</option><option value="experience">Experience</option>
              </select></label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="text-xs text-slate-400">Country</span>
              <input data-testid="pkg-form-country" value={f.country} onChange={set("country")} className="lux-input mt-1" /></label>
            <label className="block"><span className="text-xs text-slate-400">US states (codes, e.g. CO, UT)</span>
              <input data-testid="pkg-form-states" value={f.states} onChange={set("states")} disabled={f.country !== "United States"} className="lux-input mt-1 disabled:opacity-40" /></label>
          </div>
          <label className="block"><span className="text-xs text-slate-400">Trip type (booking window)</span>
            <select data-testid="pkg-form-trip-type" value={f.trip_type} onChange={set("trip_type")} className="lux-input mt-1">
              {Object.entries(tripTypes).map(([k, t]) => <option key={k} value={k}>{`${t.label} · ${t.min}–${t.max} mo`}</option>)}
            </select></label>
          {input("title", "Package name", { required: true })}
          {input("location", "Location", { required: true })}
          <label className="block"><span className="text-xs text-slate-400">Description</span>
            <textarea data-testid="pkg-form-description" rows={3} value={f.description} onChange={set("description")} className="lux-input mt-1" /></label>
          {input("image_url", "Photo URL")}
          {input("includes", "What's included (comma separated)")}
          <div className="grid grid-cols-2 gap-3">
            {input("nights", "Nights", { type: "number", min: 1, required: true })}
            {input("broker_price", "Your package price ($)", { type: "number", min: 1, step: "0.01", required: true })}
          </div>
          {budgets && <p className="text-xs text-slate-500">Base trip budget for this line: ${budgets[f.tier]?.toLocaleString()} per traveler group (Premium and family voyagers have bigger budgets).</p>}
          <div className="grid grid-cols-2 gap-3">
            {input("available_from", "Available from", { type: "date" })}
            {input("available_to", "Available until", { type: "date" })}
          </div>
          <button data-testid="pkg-form-submit" className="btn-gold w-full py-3">{initial ? "Save & resubmit for review" : "Submit for review"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
